import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  DialogTitle,
  DialogProps,
  ButtonProps,
  Divider,
  Stack,
} from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { DialogClose } from './DialogFrame/DialogClose';

type Props = {
  onClose: () => void;
  handleOk?: () => void;
  dialogContent: JSX.Element;
  dialogTitle?: string;
  customButtons?: JSX.Element | null;
  dialogProps?: DialogProps;
  headerButtonOnclick?: () => void;
  headerButton?: boolean;
  headerButtonLabel?: string;
};

const GlobalDialog = ({
  onClose,
  handleOk,
  dialogContent,
  dialogTitle,
  customButtons,
  dialogProps,
  headerButtonOnclick,
  headerButton = false,
  headerButtonLabel = '',
}: Props) => {
  return (
    <Dialog open onClose={onClose} {...dialogProps}>
      {dialogTitle && (
        <DialogTitle>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            width='100%'>
            <Typography
              className={`alignCenter ${dialogProps?.className || ''}`}>
              {dialogTitle}
            </Typography>
            {headerButton && headerButtonOnclick && (
              <Button
                sx={{minWidth: 150, marginRight: 2}}
                variant='contained'
                onClick={() => headerButtonOnclick?.()}>
                {headerButtonLabel}
              </Button>
            )}
            <DialogClose onClose={onClose} />
          </Stack>
        </DialogTitle>
      )}
      <Divider />
      <DialogContent>{dialogContent}</DialogContent>
      {customButtons && <DialogActions>{customButtons}</DialogActions>}
      {handleOk && (
        <DialogActions>
          <Button variant='contained' size='small' onClick={handleOk} autoFocus>
            OK
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GlobalDialog;

/**
 * GlobalBtnWithConfirmationDialog
 *
 * A reusable memoized button component that opens a confirmation dialog when clicked.
 * The dialog can be customized with a title, content, and submit/cancel actions.
 *
 * Props:
 * - buttonText (string, required): Text displayed on the trigger button.
 * - dialog (object, required): Dialog configuration.
 *    - dialogTitle (string): Title of the dialog.
 *    - dialogContent (JSX.Element): Content/body of the dialog.
 * - submitButton (object, required): Submit button configuration inside the dialog.
 *    - text (string, optional): Label for the submit button. Defaults to "Submit".
 *    - action (() => void, optional): Function to call when the submit button is clicked.
 *    - buttonProps (ButtonProps, optional): Additional props for the submit button.
 * - cancelButton (object, optional): Cancel button configuration inside the dialog.
 *    - text (string, optional): Label for the cancel button. Defaults to "Cancel".
 *    - buttonProps (ButtonProps, optional): Additional props for the cancel button.
 * - buttonProps (ButtonProps, optional): Additional props for the trigger button.
 *
 * Behavior:
 * - Renders a trigger button that opens a GlobalDialog.
 * - Inside the dialog:
 *    - The submit button runs `submitButton.action` (if provided) and closes the dialog.
 *    - The cancel button closes the dialog.
 * - Wrapped with React.memo to prevent unnecessary re-renders.
 */
export const GlobalBtnWithConfirmationDialog = memo(
  ({
    dialog,
    submitButton,
    cancelButton,
    buttonProps,
    buttonText,
  }: {
    buttonText: string;
    dialog: {
      dialogTitle: string;
      dialogContent: JSX.Element;
      dialogProps?: DialogProps;
    };
    submitButton: {
      text?: string;
      action?: () => void;
      buttonProps?: ButtonProps;
    };
    cancelButton?: {
      text?: string;
      buttonProps?: ButtonProps;
    };
    buttonProps?: ButtonProps;
  }) => {
    const [dialogOpen, setDialogOpen] = useState(false);

    const closeDialog = useCallback(() => setDialogOpen(false), []);

    const customButtons = useMemo(
      () => (
        <>
          <Button
            variant='contained'
            {...submitButton.buttonProps}
            onClick={() => {
              submitButton?.action?.();
              closeDialog();
            }}>
            {submitButton.text || 'Submit'}
          </Button>
          {cancelButton && (
            <Button
              variant='outlined'
              {...cancelButton.buttonProps}
              onClick={closeDialog}>
              {cancelButton.text || 'Cancel'}
            </Button>
          )}
        </>
      ),
      [submitButton, cancelButton, closeDialog],
    );

    return (
      <>
        {dialogOpen && (
          <GlobalDialog
            dialogContent={dialog.dialogContent}
            onClose={closeDialog}
            dialogTitle={dialog.dialogTitle}
            customButtons={customButtons}
            dialogProps={dialog.dialogProps}
          />
        )}

        <Button
          variant='contained'
          {...buttonProps}
          onClick={() => setDialogOpen(true)}>
          {buttonText}
        </Button>
      </>
    );
  },
);
